"use client";

/**
 * WebVitalsReporter — client component that collects Core Web Vitals
 * and reports them to analytics.
 *
 * Uses the `web-vitals` library via dynamic import for code splitting.
 * Gracefully no-ops if the library fails to load.
 *
 * Place this component inside the root layout `<body>`.
 */

import { useEffect } from "react";
import { reportWebVital, type WebVitalMetric } from "@/lib/analytics/web-vitals";

export default function WebVitalsReporter() {
  useEffect(() => {
    // Dynamic import — web-vitals is a peer dependency
    // If not installed, this silently fails
    import("web-vitals")
      .then(({ onCLS, onLCP, onFCP, onTTFB, onINP }) => {
        const handle = (metric: WebVitalMetric) => reportWebVital(metric);

        // These callbacks fire once per page load
        onCLS(handle);
        onLCP(handle);
        onFCP(handle);
        onTTFB(handle);
        onINP(handle);
      })
      .catch(() => {
        // web-vitals not installed — silently skip
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.debug(
            "[WebVitals] web-vitals library not available. Install with: pnpm add web-vitals",
          );
        }
      });
  }, []);

  return null;
}
